import React, { useState } from 'react';
import { Layout, Menu, Drawer, Button, Typography } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  DashboardOutlined,
  ShoppingOutlined,
  FileOutlined,
  LogoutOutlined,
  ProfileOutlined,
  CreditCardOutlined,
  MenuOutlined,
  TeamOutlined,
  RollbackOutlined,
  GiftOutlined
} from '@ant-design/icons';
import { logout, loginSuccess, clearOriginalAdminUser } from './../redux/userSlice';
import './AppLayout.css';
import '../styles/global.css';

const { Header, Content } = Layout;
const { Title } = Typography;

const AppLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentUser, originalAdminUser } = useSelector((state) => state.user);
  const isAdmin = currentUser?.role === 'admin';
  const [drawerVisible, setDrawerVisible] = useState(false);

  const handleMenuClick = (key) => {
    if (key === 'logout') {
      dispatch(logout());
      navigate('/login');
    } else if (key === 'backToAdmin') {
      handleBackToAdmin();
    } else {
      navigate(key);
      setDrawerVisible(false);
    }
  };

  const handleBackToAdmin = async () => {
    try {
      // Restore the original admin user
      dispatch(loginSuccess(originalAdminUser));
      localStorage.setItem('token', originalAdminUser.token);
      dispatch(clearOriginalAdminUser());
      navigate('/admin/users');
    } catch (error) {
      console.error('Error switching back to admin:', error);
    }
  };

  const adminMenuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard'
    },
    {
      key: '/admin/users',
      icon: <TeamOutlined />,
      label: 'Users'
    },
    {
      key: '/admin/leads',
      icon: <FileOutlined />,
      label: 'Manage Leads'
    },
    {
      key: '/admin/stripe-transactions',
      icon: <CreditCardOutlined />,
      label: 'Transactions'
    },
    {
      key: '/admin/promo-codes',
      icon: <GiftOutlined />,
      label: 'Promo Codes'
    },
    {
      key: '/admin/returns',
      icon: <RollbackOutlined />,
      label: 'Return Requests'
    },
    {
      key: '/profile',
      icon: <ProfileOutlined />,
      label: 'Profile'
    },
    ...(originalAdminUser ? [{
      key: 'backToAdmin',
      icon: <RollbackOutlined />,
      label: 'Back to Admin',
      onClick: handleBackToAdmin,
      style: { color: '#1890ff' }
    }] : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true
    }
  ];

  const userMenuItems = [
    {
      key: '/leads',
      icon: <FileOutlined />,
      label: 'Available Leads'
    },
    {
      key: '/purchases',
      icon: <ShoppingOutlined />,
      label: 'My Purchases'
    },
    {
      key: '/returns',
      icon: <RollbackOutlined />,
      label: 'My Returns'
    },
    {
      key: '/profile',
      icon: <ProfileOutlined />,
      label: 'Profile'
    },
    ...(originalAdminUser ? [{
      key: 'backToAdmin',
      icon: <RollbackOutlined />,
      label: 'Back to Admin',
      onClick: handleBackToAdmin,
      style: { color: '#1890ff' }
    }] : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true
    }
  ];

  return (
    <Layout className="app-layout">
      {/* Mobile Header */}
      <Header className="mobile-header">
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setDrawerVisible(true)}
          className="menu-button"
        />
        <Title level={4} className="app-title">
          Lead System
        </Title>
      </Header>

      {/* Mobile Drawer */}
      <Drawer
        title={
          <div className="drawer-header">
            <Title level={4} style={{ margin: 0, color: 'white' }}>
              Lead System
            </Title>
          </div>
        }
        placement="left"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={250}
        bodyStyle={{ padding: 0 }}
        className="mobile-drawer"
      >
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={isAdmin ? adminMenuItems : userMenuItems}
          onClick={({ key }) => handleMenuClick(key)}
        />
      </Drawer>

      {/* Desktop Sider */}
      <Layout.Sider
        breakpoint="lg"
        collapsedWidth="0"
        className="desktop-sider"
      >
        <div className="sider-header">
          <Title level={4} style={{ margin: 0, color: 'white' }}>
            Lead System
          </Title>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={isAdmin ? adminMenuItems : userMenuItems}
          onClick={({ key }) => handleMenuClick(key)}
        />
      </Layout.Sider>

      {/* Main Content */}
      <Layout className="main-content">
        <Content className="content-wrapper">
          <div className="content-container fade-in">
            {children}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout; 